import { styled } from "@linaria/react"
import { observer } from "mobx-react-lite"
import { useCallback } from "react"


import { Button } from "@/components/Button/Button.tsx"
import { AgentMessageContainer } from "@/components/Chat/AgentMessageStyles.ts"
import { MessageList } from "@/components/Chat/ChatStyles.ts"
import { UserMessageContainer } from "@/components/Chat/UserMessageStyles.ts"
import { Horizontal } from "@/components/Flex.ts"
import { ellipsize } from "@/styles/textStyles.ts"

import type { IObservableValue } from "mobx"
import type { RefObject } from "react"


export const LoadMoreButton = observer((props: {
  model: ListModel
  loadNext: boolean
  loadMorePressed: RefObject<boolean>
  loadNextText?: string
  loadPrevText?: string
}) => {
  const isLoading = props.loadNext
    ? props.model.isLoadingNext.get()
    : props.model.isLoadingPrev.get()

  const loadFunction = useCallback(() => {
    props.loadMorePressed.current = true
    const fn = props.loadNext ? props.model.loadNext : props.model.loadPrev
    return fn()
  }, [props.loadNext, props.loadMorePressed, props.model])

  if (props.loadNext && !props.model.hasNext.get()) {
    return null
  }

  if (!props.loadNext && !props.model.hasPrev.get()) {
    return null
  }

  const buttonText = props.loadNext 
    ? (props.loadNextText ?? "Load more messages") 
    : (props.loadPrevText ?? "Load previous messages")

  return (
    <ButtonContainer>
      <LoadMoreStyledButton kind="alternative" onClick={loadFunction}>
        {isLoading ? ellipsize("Loading") : buttonText}
      </LoadMoreStyledButton>
    </ButtonContainer>
  )
})

const ButtonContainer = styled(Horizontal)`
  justify-content: center;
  margin: 10px 0 30px;
`

const LoadMoreStyledButton = styled(Button)`
  color: var(--secondary-text-color);

  & + ${MessageList}, & + ${AgentMessageContainer}, & + ${UserMessageContainer} {
    margin-top: 16px;
  }
`

export type ListModel = {
  isLoadingNext: IObservableValue<boolean>
  isLoadingPrev: IObservableValue<boolean>
  hasNext: IObservableValue<boolean>
  hasPrev: IObservableValue<boolean>
  loadNext(): void
  loadPrev(): void
}
